import { useState } from 'react'
import { useToast } from '@/context/ToastContext'
import { PendingSchoolAccountService } from '@common/services/pending-school-account-service'
import { PostalCodeService } from '@common/services/postal-code-service'
import type { SchoolType } from '@common/utils/school-type'
import {
  clampPrincipalNameInput,
  clampSchoolNameInput,
  PRINCIPAL_NAME_MAX_LENGTH,
  SCHOOL_NAME_MAX_LENGTH,
} from '@common/utils/name-length'

interface RegisterData {
  schoolType: SchoolType
  schoolName: string
  postalCode: string
  prefecture: string
  city: string
  address: string
  phone: string
  principal: string
  description: string
  contactName: string
  contactEmail: string
}

interface FieldErrors {
  schoolType?: string | null
  schoolName?: string | null
  postalCode?: string | null
  prefecture?: string | null
  city?: string | null
  address?: string | null
  phone?: string | null
  principal?: string | null
  contactName?: string | null
  contactEmail?: string | null
}

export const useRegister = () => {
  const { showErrorMessage, showSuccessMessage } = useToast()
  const postalCodeService = new PostalCodeService()
  
  const [registerData, setRegisterData] = useState<RegisterData>({
    schoolType: 'public',
    schoolName: '',
    postalCode: '',
    prefecture: '',
    city: '',
    address: '',
    phone: '',
    principal: '',
    description: '',
    contactName: '',
    contactEmail: '',
  })

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isPostalCodeSearching, setIsPostalCodeSearching] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  const handleRegisterChange = (field: keyof RegisterData, value: string) => {
    setRegisterData((prev) => ({
      ...prev,
      [field]:
        field === 'schoolName'
          ? clampSchoolNameInput(value)
          : field === 'principal'
            ? clampPrincipalNameInput(value)
            : value,
    }))
  }

  const handleClearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => ({
      ...prev,
      [field]: null,
    }))
  }

  const reset = () => {
    setRegisterData({
      schoolType: 'public',
      schoolName: '',
      postalCode: '',
      prefecture: '',
      city: '',
      address: '',
      phone: '',
      principal: '',
      description: '',
      contactName: '',
      contactEmail: '',
    })
    setFieldErrors({})
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setFieldErrors({})

    // 必須項目のバリデーション（備考以外）
    const newFieldErrors: FieldErrors = {}
    let hasError = false

    if (!registerData.schoolType) {
      newFieldErrors.schoolType = '学校区分を選択してください'
      hasError = true
    }
    if (!registerData.schoolName.trim()) {
      newFieldErrors.schoolName = '学校名は必須です'
      hasError = true
    } else if (registerData.schoolName.length > SCHOOL_NAME_MAX_LENGTH) {
      newFieldErrors.schoolName = `学校名は${SCHOOL_NAME_MAX_LENGTH}文字以内で入力してください`
      hasError = true
    }
    if (!registerData.postalCode.trim()) {
      newFieldErrors.postalCode = '郵便番号は必須です'
      hasError = true
    }
    if (!registerData.prefecture.trim()) {
      newFieldErrors.prefecture = '都道府県は必須です'
      hasError = true
    }
    if (!registerData.city.trim()) {
      newFieldErrors.city = '市区町村は必須です'
      hasError = true
    }
    if (!registerData.address.trim()) {
      newFieldErrors.address = 'それ以降の住所は必須です'
      hasError = true
    }
    if (!registerData.phone.trim()) {
      newFieldErrors.phone = '電話番号は必須です'
      hasError = true
    }
    if (!registerData.principal.trim()) {
      newFieldErrors.principal = '校長名は必須です'
      hasError = true
    } else if (registerData.principal.length > PRINCIPAL_NAME_MAX_LENGTH) {
      newFieldErrors.principal = `校長名は${PRINCIPAL_NAME_MAX_LENGTH}文字以内で入力してください`
      hasError = true
    }
    if (!registerData.contactName.trim()) {
      newFieldErrors.contactName = '担当者名は必須です'
      hasError = true
    }
    if (!registerData.contactEmail.trim()) {
      newFieldErrors.contactEmail = 'メールアドレスは必須です'
      hasError = true
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerData.contactEmail)) {
      newFieldErrors.contactEmail = 'メールアドレスの形式が正しくありません'
      hasError = true
    }

    if (hasError) {
      setFieldErrors(newFieldErrors)
      return
    }

    setIsLoading(true)
    try {
      const service = new PendingSchoolAccountService()
      await service.createPendingSchoolAccount({
        schoolType: registerData.schoolType,
        schoolName: registerData.schoolName,
        postalCode: registerData.postalCode,
        prefecture: registerData.prefecture,
        city: registerData.city,
        address: registerData.address,
        phone: registerData.phone,
        principal: registerData.principal,
        description: registerData.description || undefined,
        contactName: registerData.contactName,
        contactEmail: registerData.contactEmail,
      })

      setShowSuccessModal(true)
      reset()

      return true
    } catch (err: unknown) {
      console.error('仮登録エラー:', err)
      const errorMessage = '仮登録に失敗しました。もう一度お試しください。'
      setError(errorMessage)
      showErrorMessage(errorMessage)
      return false // 失敗を返す
    } finally {
      setIsLoading(false)
    }
  }

  const handlePostalCodeSearch = async (): Promise<void> => {
    const normalizedPostalCode = registerData.postalCode.replace(/-/g, '').trim()
    if (!/^\d{7}$/.test(normalizedPostalCode)) {
      setFieldErrors((prev) => ({
        ...prev,
        postalCode: '郵便番号は7桁の数字で入力してください',
      }))
      showErrorMessage('郵便番号は7桁の数字で入力してください')
      return
    }

    try {
      setIsPostalCodeSearching(true)
      const result = await postalCodeService.lookupPostalCode(normalizedPostalCode)
      const firstCandidate = result.candidates[0]
      if (!firstCandidate) {
        showErrorMessage('該当する住所が見つかりませんでした')
        return
      }

      setRegisterData((prev) => ({
        ...prev,
        postalCode: normalizedPostalCode,
        prefecture: firstCandidate.prefecture,
        city: firstCandidate.city,
        address: firstCandidate.address,
      }))
      setFieldErrors((prev) => ({
        ...prev,
        postalCode: null,
        prefecture: null,
        city: null,
        address: null,
      }))

      if (result.candidates.length > 1) {
        showSuccessMessage('候補が複数あるため先頭の住所を反映しました。必要に応じて修正してください。')
      } else {
        showSuccessMessage('住所を自動入力しました。')
      }
    } catch (err: unknown) {
      console.error('郵便番号検索エラー:', err)
      const code = (err instanceof Error ? err.message : '').toLowerCase()
      if (code.includes('not-found')) {
        showErrorMessage('該当する住所が見つかりませんでした')
        return
      }
      if (code.includes('invalid-argument')) {
        showErrorMessage('郵便番号は7桁の数字で入力してください')
        return
      }
      showErrorMessage('郵便番号検索に失敗しました。しばらくして再度お試しください。')
    } finally {
      setIsPostalCodeSearching(false)
    }
  }

  const clearError = () => {
    setError('')
  }

  const closeSuccessModal = () => {
    setShowSuccessModal(false)
  }

  return {
    registerData,
    fieldErrors,
    error,
    isLoading,
    showSuccessModal,
    isPostalCodeSearching,
    handleRegisterChange,
    handlePostalCodeSearch,
    handleClearFieldError,
    handleSubmit,
    reset,
    clearError,
    closeSuccessModal,
  }
}

